import SEOMeta, { BOOK_META } from '../../../components/SEOMeta';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useReaderAuth } from '../../../context/ReaderAuthContext';
import { BookOpen, ChevronRight, Loader2, LogOut, User, Lock, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BOOK_SLUG = 'cultural-fluency-without-compromise';
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01';

const BookLibraryPage = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, hasEbookAccess, logout, isLoading: authLoading } = useReaderAuth();
  
  const [bookMeta, setBookMeta] = useState(null);
  const [progress, setProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Auth check
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated()) {
        navigate(`/signin?redirect=/books/${BOOK_SLUG}/library`);
      }
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch book data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        // Fetch book metadata
        const metaRes = await fetch(`${API_URL}/api/ebook/metadata`);
        if (metaRes.ok) {
          const data = await metaRes.json();
          setBookMeta(data);
        }
        
        // Fetch reading progress if user has access
        if (hasEbookAccess()) {
          const progressRes = await fetch(`${API_URL}/api/ebook/progress`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (progressRes.ok) {
            const data = await progressRes.json();
            setProgress(data);
          }
        }
      } catch (e) {
        console.error('Failed to fetch library data:', e);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [token, hasEbookAccess]);

  const handleLogout = () => {
    logout();
    navigate(`/books/${BOOK_SLUG}`);
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  const chapters = bookMeta?.table_of_contents?.filter(item => item.type === 'chapter') || [];
  const hasAccess = hasEbookAccess();

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SEOMeta 
        url="/books/cultural-fluency-without-compromise/library"
        title="My Library — Cultural Fluency Without Compromise"
        description="Access your library and reading progress for Cultural Fluency Without Compromise."
        image={BOOK_META.image}
        imageAlt={BOOK_META.imageAlt}
        noIndex={true}
      />
      {/* Header */}
      <header className="bg-[#2c3e50] text-white py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to={`/books/${BOOK_SLUG}`} className="flex items-center gap-2 text-[#d4af37] hover:text-white transition-colors">
            <BookOpen size={24} />
            <span className="font-semibold">My Library</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-white/80">
              <User size={18} />
              <span className="text-sm hidden sm:inline">{user?.email}</span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Sign out"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Library Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Book Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="md:flex">
            {/* Book Cover */}
            <div className="md:w-1/3 bg-gradient-to-br from-[#2c3e50] to-[#1a252f] p-8 flex items-center justify-center">
              <div className="text-center text-white">
                <p className="text-xs tracking-[0.2em] text-[#d4af37] mb-4">A BOOK BY</p>
                <h2 className="text-xl font-bold mb-1">CULTURAL FLUENCY</h2>
                <h2 className="text-xl font-bold mb-3">WITHOUT COMPROMISE</h2>
                <div className="w-12 h-0.5 bg-[#d4af37] mx-auto mb-3" />
                <p className="text-xs text-white/70 mb-4">Thriving Across Cultures with Presence and Integrity</p>
                <p className="text-xs tracking-[0.15em] text-[#d4af37]">PROMISE MONDAY</p>
              </div>
            </div>
            
            {/* Book Info */}
            <div className="md:w-2/3 p-6 md:p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-[#2c3e50] mb-1">Cultural Fluency Without Compromise</h1>
                  <p className="text-[#5a6c7d]">by Promise Monday</p>
                </div>
                {hasAccess ? (
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                    Owned
                  </span>
                ) : (
                  <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm">
                    Preview Only
                  </span>
                )}
              </div>
              
              {hasAccess && progress && (
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-[#5a6c7d]">Reading Progress</span>
                    <span className="text-[#2c3e50] font-medium">{Math.round(progress.percent_complete || 0)}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#d4af37] rounded-full transition-all"
                      style={{ width: `${progress.percent_complete || 0}%` }}
                    />
                  </div>
                </div>
              )}
              
              {hasAccess ? (
                <Link
                  to={`/books/${BOOK_SLUG}/read/${progress?.current_chapter_id || chapters[0]?.id || 'preface'}`}
                  className="inline-flex items-center gap-2 bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors"
                  data-testid="continue-reading-btn"
                >
                  <BookOpen size={20} />
                  {progress ? 'Continue Reading' : 'Start Reading'}
                </Link>
              ) : (
                <a
                  href={STRIPE_PAYMENT_LINK}
                  className="inline-flex items-center gap-2 bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors"
                  data-testid="buy-access-btn"
                >
                  <ShoppingCart size={20} />
                  Buy Full Access – £9.99
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-bold text-[#2c3e50]">Table of Contents</h2>
          </div>
          
          <div className="divide-y divide-gray-100">
            {chapters.map((chapter) => {
              const isPreview = chapter.id.toLowerCase().includes('preface') || chapter.id.toLowerCase().includes('chapter-1');
              const canAccess = hasAccess || isPreview;
              
              return (
                <div key={chapter.id}>
                  {canAccess ? (
                    <Link
                      to={`/books/${BOOK_SLUG}/read/${chapter.id}`}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group"
                      data-testid={`toc-item-${chapter.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {chapter.number && (
                          <span className="text-sm text-[#d4af37] font-medium w-8">
                            {chapter.number}.
                          </span>
                        )}
                        <span className="text-[#2c3e50] group-hover:text-[#d4af37] transition-colors">
                          {chapter.title}
                        </span>
                        {!hasAccess && isPreview && (
                          <span className="text-xs bg-[#d4af37]/10 text-[#d4af37] px-2 py-0.5 rounded">
                            Preview
                          </span>
                        )}
                      </div>
                      <ChevronRight size={18} className="text-gray-400 group-hover:text-[#d4af37] transition-colors" />
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between p-4 opacity-50">
                      <div className="flex items-center gap-3">
                        {chapter.number && (
                          <span className="text-sm text-[#5a6c7d] font-medium w-8">
                            {chapter.number}.
                          </span>
                        )}
                        <span className="text-[#5a6c7d]">{chapter.title}</span>
                      </div>
                      <Lock size={16} className="text-[#d4af37]" />
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* About the Author */}
            <div className="flex items-center justify-between p-4 opacity-50">
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#5a6c7d] font-medium w-8"></span>
                <span className="text-[#5a6c7d]">About the Author</span>
              </div>
              {hasAccess ? (
                <ChevronRight size={18} className="text-gray-400" />
              ) : (
                <Lock size={16} className="text-[#d4af37]" />
              )}
            </div>
          </div>
        </div>

        {/* ISBN Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-[#5a6c7d]">ISBN (eBook): 978-1-918265-02-6</p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2c3e50] text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link 
            to={`/books/${BOOK_SLUG}`}
            className="text-[#d4af37] hover:underline"
          >
            ← Back to Book
          </Link>
          <p className="text-sm text-white/50 mt-4">
            © {new Date().getFullYear()} Third Culture Leadership. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookLibraryPage;
